import React, { useState } from "react";
import toast from "react-hot-toast";
import styled from "styled-components";

import Button from "../button/Button";
import ToolTip from "../tooltip/ToolTip";
import {
  useFollowUserMutation,
  useUnfollowUserMutation,
} from "../../app/services/follows";
import { getAuthTokenFromCookies } from "../../common/utils";

export interface FollowButtonProps {
  /** フォロー対象の AuthUser ID。 */
  userId: number;
  /** 初期フォロー状態。 */
  isFollowing: boolean;
  /** 表示名（トースト文言用）。 */
  displayName?: string;
  size?: "small" | "medium" | "large";
  /**
   * 表示形態。
   * - `button`（既定）: テキスト付きの通常ボタン。
   * - `bookmark`: ユーザーリスト / マイページと同じ bookmark アイコンボタン
   *   （ToolTip「フォローする」/「フォローを解除」付き）。
   */
  variant?: "button" | "bookmark";
  className?: string;
}

// ユーザーリスト（UserListRow の FollowingActionButton）と同じ見た目の
// 32px 円形 bookmark ボタン。フォロー中は青 + FILL。
const BookmarkIconButton = styled.button<{ $isFollowing: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  .material-symbols-rounded {
    font-size: 24px;
    color: ${(props) =>
      props.$isFollowing ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
    font-variation-settings: ${(props) =>
      props.$isFollowing ? "'FILL' 1" : "'FILL' 0"};
    transition:
      color 0.2s ease,
      scale 0.2s ease;
  }

  &:hover .material-symbols-rounded {
    color: var(--soremo-blue);
    scale: 1.1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * フォロー / フォロー解除ボタン（グローバルコンポーネント）。
 *
 * 公開プロフィールページなど、未ログイン環境からも描画される。未ログイン時に
 * 押下した場合は本体アプリのサインインへ誘導する。ログイン中は楽観的に表示を
 * 切り替えつつ `followsApi` の mutation を呼ぶ。
 */
const FollowButton: React.FC<FollowButtonProps> = ({
  userId,
  isFollowing,
  displayName,
  size = "medium",
  variant = "button",
  className,
}) => {
  const [following, setFollowing] = useState<boolean>(isFollowing);
  const [followUser, { isLoading: isFollowingLoading }] = useFollowUserMutation();
  const [unfollowUser, { isLoading: isUnfollowingLoading }] = useUnfollowUserMutation();
  const isLoading = isFollowingLoading || isUnfollowingLoading;

  const handleClick = async () => {
    // 未ログイン時はサインインへ誘導（公開ページからのフォロー導線）。
    // signin ページはサインイン成功後に `redirect` クエリのパスへ戻す。
    if (!getAuthTokenFromCookies()) {
      const redirect = encodeURIComponent(window.location.pathname);
      window.location.assign(`/signin?redirect=${redirect}`);
      return;
    }

    const nextFollowing = !following;
    setFollowing(nextFollowing); // 楽観的更新
    try {
      if (nextFollowing) {
        await followUser(userId).unwrap();
        toast.success(
          displayName ? `${displayName} をフォローしました` : "フォローしました",
        );
      } else {
        await unfollowUser(userId).unwrap();
        toast.success("フォローを解除しました");
      }
    } catch {
      setFollowing(!nextFollowing); // 失敗時はロールバック
      toast.error("通信に失敗しました。時間をおいて再度お試しください。");
    }
  };

  if (variant === "bookmark") {
    const label = following ? "フォローを解除" : "フォローする";
    return (
      <ToolTip description={label}>
        <BookmarkIconButton
          type="button"
          $isFollowing={following}
          disabled={isLoading}
          onClick={handleClick}
          aria-label={label}
          aria-pressed={following}
          className={className}
        >
          <span className="material-symbols-rounded">bookmark</span>
        </BookmarkIconButton>
      </ToolTip>
    );
  }

  return (
    <Button
      theme="primary"
      variant={following ? "outlined" : "filled"}
      size={size}
      width="fit"
      leftIcon={following ? "check" : "add"}
      disabled={isLoading}
      onClick={handleClick}
      className={className}
    >
      {following ? "フォロー中" : "フォロー"}
    </Button>
  );
};

export default FollowButton;
